import { takeLatest, call, put, select } from 'redux-saga/effects';

// Services
import { benefitsApi } from 'services/apiDigitalTicket/benefits';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Helpers
import { errorMessagersList } from 'helpers/errorMessagers';

// Constants
import * as BenefitsActions from './actions';
import { BenefitsTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof BenefitsActions.listBenefitsRequest>): any {
  try {
    const { params, loading = true } = payload;

    if (loading) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando políticas' }),
      );
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(benefitsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...params },
    });

    if (response.status === 200) {
      yield put(
        BenefitsActions.listBenefitsSuccess({
          benefitsList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (loading) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('benefits - list - error', error);
    yield put(BenefitsActions.listBenefitsFailure());

    // carga auxiliar (ex.: limites no extrato): falha em silêncio
    if (payload?.silent) return;

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(DialogsActions.setClose());
  }
}

function* listLines({
  payload,
}: ReturnType<typeof BenefitsActions.listBenefitLinesRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.listLines, {
      params: {
        transportOperatorId: transportOperator.id,
        ...payload,
      },
    });

    if (response.status === 200) {
      yield put(
        BenefitsActions.listBenefitLinesSuccess({
          benefitLines: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('benefits - listBenefitLines - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.listBenefitLinesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
  meta,
}: ReturnType<typeof BenefitsActions.getBenefitRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BenefitsActions.getBenefitSuccess({ benefitData: response.data }),
      );
      yield put(DialogsActions.setClose());
      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('benefits - get - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.getBenefitFailure());
    yield put(DialogsActions.setClose());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof BenefitsActions.createBenefitRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Criando Política Tarifária' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BenefitsActions.createBenefitSuccess({ benefitCreated: response.data }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Política Tarifária criada com sucesso',
        }),
      );

      yield put(BenefitsActions.setCurrentOperation({ operation: 'none' }));
      yield put(BenefitsActions.listBenefitsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('benefit - createBenefit - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.createBenefitFailure());
    yield put(DialogsActions.setClose());
  }
}

function* attach({
  payload,
}: ReturnType<typeof BenefitsActions.attachBenefitRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atrelando Política Tarifária' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.attach, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        BenefitsActions.attachBenefitSuccess({ attachBenefit: response.data }),
      );
      yield put(DialogsActions.setClose());
      if (!payload.isBenefit) {
        yield put(
          BenefitsActions.setCurrentOperation({
            operation: 'none',
          }),
        );
      }
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Política Tarifária atrelada com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('benefit - attachBenefit - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.attachBenefitFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof BenefitsActions.updateBenefitRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(BenefitsActions.updateBenefitSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Política Tarifária alterada com sucesso',
        }),
      );

      yield put(BenefitsActions.setCurrentOperation({ operation: 'none' }));
      yield put(
        BenefitsActions.listBenefitsRequest({ params: { _limit: 9999 } }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - updateBenefit - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.updateBenefitFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof BenefitsActions.deleteBenefitRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo Política Tarifária' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(benefitsApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Política Tarifária excluída com sucesso',
        }),
      );

      yield put(BenefitsActions.setCurrentOperation({ operation: 'none' }));
      yield put(BenefitsActions.listBenefitsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('profiles - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(BenefitsActions.listBenefitsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const benefitsSagas = [
  takeLatest(BenefitsTypes.LIST_BENEFITS_REQUEST, list),
  takeLatest(BenefitsTypes.LIST_BENEFIT_LINES_REQUEST, listLines),
  takeLatest(BenefitsTypes.GET_BENEFIT_REQUEST, get),
  takeLatest(BenefitsTypes.CREATE_BENEFIT_REQUEST, create),
  takeLatest(BenefitsTypes.ATTACH_BENEFIT_REQUEST, attach),
  takeLatest(BenefitsTypes.UPDATE_BENEFIT_REQUEST, update),
  takeLatest(BenefitsTypes.DELETE_BENEFIT_REQUEST, remove),
];
